'use client';
import React from 'react';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import Section from '@repo/ui/src/components/Section';
import ExpandableContentWithMarkdown from '@repo/ui/src/components/ExpandableContentWithMarkdown';
import { FlexDirection, HeaderVariationProps } from '@repo/ui/src/types';
import dynamic from 'next/dynamic';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import { classNames } from '@repo/ui/src/utils/classNames';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

interface Props {
  toutContent: Record<string, any>;
  markdownHeading: string;
  markdownHeaderVariation?: HeaderVariationProps;
  markdown: string;
  flexDisplay: FlexDirection;
  forceDivider: boolean;
  expandableTitle: string;
  disclaimerText: string;
  expandableMarkdown: string;
}

export const ToutContentWithMarkdown = ({
  toutContent,
  markdownHeading,
  markdownHeaderVariation,
  markdown,
  flexDisplay,
  forceDivider,
  expandableTitle,
  disclaimerText,
  expandableMarkdown,
}: Props) => {
  const mdAndUp = useIsMD();
  const computedFlexDirection = flexDisplay || (mdAndUp ? 'row' : 'column');
  const MarkdownHeaderEl = markdownHeaderVariation?.headerVariation || 'p';

  const markdownComponents = {
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol className="list-none m-0 pt-2">{children}</ol>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li className="pb-2 relative leading-[32px] tra-text-web-body-xl">{children}</li>
    ),
  };

  return (
    <Section texture gutterX gutterY basePadding>
      <div>
        <div
          className={classNames(
            'flex justify-between',
            computedFlexDirection === 'column' ? 'flex-col' : 'flex-row',
          )}
        >
          <div className="pb-4 max-w-[36rem] md:pr-16 lg:max-w-[40vw]">
            <ToutContent
              divider
              headingStyleVariant="h2"
              align="left"
              forceDivider={forceDivider}
              {...(toutContent as any)}
            />
          </div>
          <div className="pt-1 min-w-[30vw] xl:min-w-[40vw]">
            {markdownHeading && (
              <MarkdownHeaderEl className="tra-text-web-heading-6 font-semibold mb-4">
                {markdownHeading}
              </MarkdownHeaderEl>
            )}
            {markdown && (
              <Markdown bodySize="xLarge" components={markdownComponents}>
                {markdown}
              </Markdown>
            )}
          </div>
        </div>
        {expandableMarkdown && (
          <div className="w-full">
            <ExpandableContentWithMarkdown
              title={expandableTitle}
              markdown={expandableMarkdown}
              disclaimerText={disclaimerText}
              markdownComponents={markdownComponents}
            />
          </div>
        )}
      </div>
    </Section>
  );
};

export default ToutContentWithMarkdown;
